// PRD 05 §2.1 — right-side editor panel: item header (name + code/cycle/day)
// followed by two parallel columns (환자용 / 보호자용). When no item is
// selected, shows the centered "select an item" prompt from the prototype.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, MousePointerClick } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';
import { InfoModal } from '@/components/feedback/InfoModal';
import { useDeployDirtyStore } from '@/features/pro-deployment/store/deploy-dirty.store';
import { useCan } from '@/features/roles/hooks/use-can';

import { useProTreeQuery } from '../hooks/use-questionnaire-tree';
import { useItemQuestionsQuery } from '../hooks/use-item-questions';
import { useDiseaseFilterOptionsQuery } from '../hooks/use-disease-filter-options';
import { useDeleteQuestionMutation } from '../hooks/use-question-mutations';
import { useQuestionnaireDesignPageStore } from '../store/questionnaire-design-page.store';
import {
  buildDiseaseLookup,
  itemMatchesDiseaseFilter,
} from '../utils/disease-filter';
import { findItemNode } from '../utils/pro-tree';
import { groupQuestionsForRender } from '../utils/question-layout';
import {
  QuestionnaireDesignApiError,
  type Applicability,
  type Question,
} from '../types/questionnaire-design.types';
import { CopyQuestionModal } from './CopyQuestionModal';
import { DeleteQuestionConfirm } from './DeleteQuestionConfirm';
import { MobilePreviewModal } from './MobilePreviewModal';
import { PostSaveDeployNotice } from './PostSaveDeployNotice';
import { QuestionColumn } from './QuestionColumn';
import { QuestionEditPanel } from './QuestionEditPanel';

function EmptyEditor() {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '80px 0',
        color: 'var(--muted-foreground)',
      }}
    >
      <MousePointerClick size={40} style={{ marginBottom: 'var(--space-4)' }} />
      <p style={{ fontSize: 'var(--font-size-sm)' }}>
        {t('editor.emptyHint')}
      </p>
    </div>
  );
}

function LoadingEditor() {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 80,
        color: 'var(--muted-foreground)',
        fontSize: 'var(--font-size-sm)',
        gap: 8,
      }}
    >
      <Loader2 size={16} className="animate-spin" /> {t('editor.loading')}
    </div>
  );
}

function ErrorEditor({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 80,
        gap: 12,
        textAlign: 'center',
      }}
    >
      <AlertTriangle size={32} color="var(--destructive)" />
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--foreground)', margin: 0 }}>
        {t('editor.error')}
      </p>
      <button type="button" className="btn btn-sm btn-secondary" onClick={onRetry}>
        {t('editor.retry')}
      </button>
    </div>
  );
}

export function QuestionnaireEditorPanel() {
  const { t } = useTranslation('questionnaire-design');
  // SHCT-432 — cycle/day arrive as codes; map them to Korean labels via the
  // pro-item-design i18n catalog (single source for the schedule vocabulary).
  const { t: tSchedule } = useTranslation('pro-item-design');
  const selectedItemId = useQuestionnaireDesignPageStore(
    (s) => s.selectedItemId,
  );
  const openCreatePanel = useQuestionnaireDesignPageStore((s) => s.openCreatePanel);
  const openEditPanel = useQuestionnaireDesignPageStore((s) => s.openEditPanel);
  const openAddSubPanel = useQuestionnaireDesignPageStore((s) => s.openAddSubPanel);
  const openCopyModal = useQuestionnaireDesignPageStore((s) => s.openCopyModal);
  const openCopySubModal = useQuestionnaireDesignPageStore((s) => s.openCopySubModal);
  const diseaseFilter = useQuestionnaireDesignPageStore((s) => s.diseaseFilter);
  const addSurveyDirty = useDeployDirtyStore((s) => s.addSurvey);
  const { data: diseaseOptions } = useDiseaseFilterOptionsQuery();
  const deleteMutation = useDeleteQuestionMutation();
  // PRD 05 §7 — READ users see the page but can't add/edit/copy/delete. WRITE
  // controls disappear (rather than rendering disabled) to keep the column
  // header compact and match the disease-code feature's gating pattern.
  const canWrite = useCan('PRO_MGMT', 'WRITE');

  // Local delete-flow state. Kept here (not in the page store) because the
  // delete confirm is item-scoped and resets as soon as the user navigates
  // to another item — no need to persist across selection changes.
  const [pendingDeleteQid, setPendingDeleteQid] = useState<string | null>(null);
  const [postDeleteOpen, setPostDeleteOpen] = useState(false);
  // Business-error (409) message when a delete is blocked because the question
  // is still a branch target — surfaced as a Modal per §7.1, not a toast.
  const [deleteBlockedMsg, setDeleteBlockedMsg] = useState<string | null>(null);
  // Local preview-flow state. Same rationale as delete — no benefit to
  // surviving navigation.
  const [previewStartQid, setPreviewStartQid] = useState<string | null>(null);

  const { data: tree } = useProTreeQuery();
  const {
    data: questions,
    isLoading: questionsLoading,
    isError: questionsError,
    refetch: refetchQuestions,
  } = useItemQuestionsQuery(selectedItemId);

  const item = selectedItemId && tree
    ? findItemNode(tree, selectedItemId)
    : null;

  // SHCT-517 / [ADM.046] — the Scope every card in both columns displays. Read off the item, not off
  // `question.applicability`: the server sends both and they can disagree on rows saved before the
  // question stopped owning a Scope, and only the item's value follows a later Scope change.
  // `null` for a server build older than the field — the cards then fall back to the stored value.
  const itemScope = (item?.scope as Applicability | undefined) ?? null;

  // Schedule codes → Korean labels. Cycle: single code. Day: join the selected
  // weekday codes (e.g. 월, 수, 금); empty → dash. (Range form e.g. 월~일 not
  // compressed — pending BA confirmation.)
  const cycleLabel = item?.cycle
    ? tSchedule(`item.fields.cycle.options.${item.cycle}`)
    : t('editor.metaDash');
  const dayLabel =
    item?.days && item.days.length > 0
      ? item.days.map((d) => tSchedule(`item.fields.daysOfWeek.${d}`)).join(', ')
      : t('editor.metaDash');

  const patientNodes = useMemo(
    () => (questions ? groupQuestionsForRender(questions, 'PATIENT') : []),
    [questions],
  );
  const caregiverNodes = useMemo(
    () => (questions ? groupQuestionsForRender(questions, 'CAREGIVER') : []),
    [questions],
  );

  // qid → "N" or "N-M" lookup so BranchChip can render the JUMP target
  // number the author already sees on the destination card.
  const jumpDisplayNumByQid = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of [...patientNodes, ...caregiverNodes]) {
      map.set(node.question.qid, node.displayNum);
      for (const sub of node.children) {
        map.set(sub.question.qid, sub.displayNum);
      }
    }
    return map;
  }, [patientNodes, caregiverNodes]);

  // SHCT-250 AC-10 / SHCT-247 BR-Rvalue-01 — non-blocking warning when the
  // selected item has questions but none contribute to the R-value calculation.
  const allContributionOff = useMemo(
    () =>
      !!questions &&
      questions.length > 0 &&
      questions.every((q) => !q.advanced.scoreContribution),
    [questions],
  );

  const diseaseLookup = useMemo(
    () =>
      buildDiseaseLookup(
        (diseaseOptions ?? []).map((o) => o.group),
        new Map((diseaseOptions ?? []).map((o) => [o.group.id, o.codes])),
      ),
    [diseaseOptions],
  );

  // [ADM.046] — the filter matches on the ITEM's Scope, so every question under one item shares the
  // same verdict. SHCT-239 BR-01's per-question dimming therefore collapses to an item-level one:
  // either the whole panel is in scope or none of it is, which the overlay below already expresses.
  // `item` above is the same lookup — reuse it rather than walking the tree twice.
  // SHCT-239 (G15, Option 1) — when the selected item is outside the active disease filter the panel
  // is covered by a non-interactive overlay so its questions can't be edited (the tree item is also
  // non-clickable).
  //
  // [ADM.047] changed the test: 질병 필터 판정에 **보유 문항 수를 쓰지 않는다**. It used to read "has
  // questions but none match", which let an out-of-scope item stay editable simply because it was
  // empty. Now an item is excluded purely on its Scope — and an in-scope item with zero questions
  // stays editable so the first question can be written.
  const selectedItemExcluded = useMemo(() => {
    if (!diseaseFilter || !item) return false;
    return !itemMatchesDiseaseFilter(item, diseaseFilter, diseaseLookup);
  }, [diseaseFilter, item, diseaseLookup]);

  // Nothing is dimmed per-question any more — see above.
  const isDimmed = undefined;

  const pendingDeleteIsSub = useMemo(
    () =>
      !!pendingDeleteQid &&
      !!questions?.find((q) => q.qid === pendingDeleteQid)?.parentQid,
    [pendingDeleteQid, questions],
  );

  const confirmDelete = async () => {
    if (!pendingDeleteQid || !selectedItemId) return;
    try {
      await deleteMutation.mutateAsync({
        qid: pendingDeleteQid,
        itemId: selectedItemId,
      });
      addSurveyDirty(pendingDeleteQid);
      setPendingDeleteQid(null);
      toast.success(t('deleteConfirm.toastSuccess'));
      setPostDeleteOpen(true);
    } catch (e) {
      // §7.1 — a blocked delete (branch reference) is a Business Logic error and
      // must be shown as a Modal; everything else stays a toast (System error).
      if (
        e instanceof QuestionnaireDesignApiError &&
        e.code === 'QUESTION_REFERENCED_BY_BRANCH'
      ) {
        setPendingDeleteQid(null);
        setDeleteBlockedMsg(e.message || t('deleteConfirm.referencedByBranch'));
        return;
      }
      const msg = e instanceof Error ? e.message : t('deleteConfirm.toastError');
      toast.error(msg);
    }
  };

  return (
    <section
      style={{
        flex: 1,
        overflowY: 'auto',
        background: 'var(--background)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-4) var(--space-6)',
          borderBottom: '1px solid var(--border)',
          position: 'sticky',
          top: 0,
          background: 'var(--background)',
          zIndex: 10,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--foreground)',
            }}
          >
            {item ? item.label : t('editor.selectItem')}
          </span>
          {item ? (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--muted-foreground)',
                marginLeft: 'var(--space-3)',
              }}
            >
              {t('editor.metaLabelCode')}: {item.code} |{' '}
              {t('editor.metaLabelCycle')}: {cycleLabel} |{' '}
              {t('editor.metaLabelDay')}: {dayLabel}
            </span>
          ) : null}
        </div>
      </header>

      <div style={{ padding: 'var(--space-6)' }}>
        {!item ? (
          <EmptyEditor />
        ) : questionsLoading ? (
          <LoadingEditor />
        ) : questionsError ? (
          <ErrorEditor onRetry={() => refetchQuestions()} />
        ) : (
          <>
          {allContributionOff ? (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-4)',
                padding: 'var(--space-3) var(--space-4)',
                border: '1px solid var(--warning, #f0ad4e)',
                borderRadius: 'var(--radius-md)',
                background: '#fff8ec',
                color: 'var(--foreground)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <AlertTriangle size={16} color="var(--warning, #f0ad4e)" style={{ flexShrink: 0 }} />
              {t('editor.allContributionOffWarning')}
            </div>
          ) : null}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              gap: 0,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              background: 'var(--card)',
            }}
          >
            <QuestionColumn
              target="PATIENT"
              nodes={patientNodes}
              jumpDisplayNumByQid={jumpDisplayNumByQid}
              canWrite={canWrite}
              itemScope={itemScope}
              onAdd={() => openCreatePanel('PATIENT')}
              onCopy={() => openCopyModal('PATIENT')}
              onEdit={openEditPanel}
              onAddSub={openAddSubPanel}
              onCopyAsSub={(parentQid) => openCopySubModal(parentQid, 'PATIENT')}
              onDelete={setPendingDeleteQid}
              onPreview={setPreviewStartQid}
              isDimmed={isDimmed}
            />
            <div
              style={{ width: 1, background: 'var(--border)', flexShrink: 0 }}
            />
            <QuestionColumn
              target="CAREGIVER"
              nodes={caregiverNodes}
              jumpDisplayNumByQid={jumpDisplayNumByQid}
              canWrite={canWrite}
              itemScope={itemScope}
              onAdd={() => openCreatePanel('CAREGIVER')}
              onCopy={() => openCopyModal('CAREGIVER')}
              onEdit={openEditPanel}
              onAddSub={openAddSubPanel}
              onCopyAsSub={(parentQid) => openCopySubModal(parentQid, 'CAREGIVER')}
              onDelete={setPendingDeleteQid}
              onPreview={setPreviewStartQid}
              isDimmed={isDimmed}
            />
            {/* SHCT-239 (G15, Option 1) — item not in the active disease code:
                cover the columns with a non-interactive layer so its questions
                can't be edited until a matching disease code is selected. */}
            {selectedItemExcluded ? (
              <div
                role="note"
                aria-label={t('editor.filterExcludedMessage')}
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-6)',
                  textAlign: 'center',
                  cursor: 'not-allowed',
                  background: 'rgba(250, 250, 250, 0.72)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    maxWidth: 360,
                    color: 'var(--muted-foreground)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <AlertTriangle size={22} color="var(--warning, #f0ad4e)" />
                  {t('editor.filterExcludedMessage')}
                </div>
              </div>
            ) : null}
          </div>
          </>
        )}
      </div>

      {/* The slide-panel + delete confirm + copy modal only mount for WRITE
          users — defense-in-depth on top of the button-level gating above. */}
      {canWrite && selectedItemId ? (
        <QuestionEditPanel selectedItemId={selectedItemId} />
      ) : null}

      {canWrite ? (
        <>
          <DeleteQuestionConfirm
            open={pendingDeleteQid !== null}
            isSub={pendingDeleteIsSub}
            loading={deleteMutation.isPending}
            onConfirm={confirmDelete}
            onCancel={() => setPendingDeleteQid(null)}
          />

          <PostSaveDeployNotice
            open={postDeleteOpen}
            action="deleted"
            onClose={() => setPostDeleteOpen(false)}
          />

          <CopyQuestionModal />
        </>
      ) : null}

      <MobilePreviewModal
        open={previewStartQid !== null}
        questions={
          // Walk only the column the preview started in — patients and
          // caregivers see different question flows in the real app.
          previewStartQid && questions
            ? questions.filter(
                (q) =>
                  q.target ===
                  questions.find((x) => x.qid === previewStartQid)?.target,
              )
            : []
        }
        startQid={previewStartQid}
        onClose={() => setPreviewStartQid(null)}
      />

      <InfoModal
        open={deleteBlockedMsg !== null}
        title={t('deleteConfirm.blockedTitle')}
        body={deleteBlockedMsg ?? ''}
        onClose={() => setDeleteBlockedMsg(null)}
      />
    </section>
  );
}
